import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { FormBuilder } from '@/components/formBuilder';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalBodyProps,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalFooter,
  ModalHeader,
  ModalHeaderProps,
  ModalFooterProps,
  ModalOverlay,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';

interface ModalWithFormProps {
  isOpen?: boolean;
  onClose?: () => void;
  scrollOnError?: boolean;
  headerText?: React.ReactNode;
  subHeaderText?: any;
  primaryCtaText?: string;
  isCtaLoading?: boolean;
  handleSubmit?: any;
  formConfig?: any[];
  initialValues?: Record<string, any>;
  showAdditionalBlock?: boolean;
  AdditionalBlock?: React.ReactNode;
  showSecondaryButton?: boolean;
  secondaryCtaText?: string;
  handleSecondaryClick?: () => void;
  formContainerStyles?: Record<string, any>;
  withForm?: boolean;
  showAdditionalBlockAfter?: boolean;
  AdditionalBlockAfter?: React.ComponentType<any>;
  additionalBlockAfterProps?: Record<string, any>;
  children?: ReactNode;
  modalContentProps?: ModalContentProps;
  autoFocus?: boolean;
  showFooter?: boolean;
  modalRef?: any;
  renderHeaderCta?: any;
  showHeader?: boolean;
  showCloseButton?: boolean;
  modalBodyProps?: ModalBodyProps;
  modalHeaderProps?: ModalHeaderProps;
  modalFooterProps?: ModalFooterProps;
  isLoading?: boolean;
  enableBorder?: boolean;
  customFormRef?: React.RefObject<HTMLFormElement>;
  showOverlay?: boolean;
  onCancel?: (e: any) => void;
  modalOverlayProps?: React.ComponentProps<typeof ModalOverlay>;
  skipDirtyCheck?: boolean;
  modalSize?: React.ComponentProps<typeof Modal>['size'];
  skipCTADisabled?: boolean;
  showCancelButton?: boolean;
  // Pass-through to FormBuilder. Set both `observFunc` and
  // `isObserveEnabled` to be notified of every form-values change —
  // useful when the parent wants to react to a field selection
  // (e.g., MoveToAnotherStage shows an inline automation preview
  // when the user picks a stage with an active workflow_rule).
  observFunc?: (values: Record<string, unknown>) => void;
  isObserveEnabled?: boolean;
  // Style overrides spread onto the secondary footer button. Useful
  // when the secondary action needs a non-default surface (e.g. the
  // purple "Move without automation" CTA in MoveToAnotherStage).
  secondaryButtonProps?: React.ComponentProps<typeof Button>;
}

export const ModalWithForm: React.FC<ModalWithFormProps> = ({
  isOpen = false,
  onClose = () => {},
  onCancel = (_e: any) => {},
  scrollOnError = false,
  headerText = undefined,
  subHeaderText = undefined,
  primaryCtaText = '',
  isCtaLoading = false,
  handleSubmit = undefined,
  formConfig = [],
  initialValues = {},
  showAdditionalBlock = false,
  AdditionalBlock = null,
  showSecondaryButton = false,
  secondaryCtaText = '',
  handleSecondaryClick = () => {},
  formContainerStyles = {},
  withForm = true,
  renderHeaderCta = undefined,
  showAdditionalBlockAfter = false,
  AdditionalBlockAfter = null,
  additionalBlockAfterProps = {},
  children = undefined,
  autoFocus = true,
  showFooter = true,
  showCloseButton = true,
  showHeader = true,
  modalContentProps = {},
  modalRef = undefined,
  enableBorder = false,
  modalBodyProps = {},
  modalHeaderProps = {},
  modalFooterProps = {},
  customFormRef = null,
  showOverlay = true,
  modalOverlayProps = {},
  skipDirtyCheck = false,
  skipCTADisabled = false,
  modalSize = 'sm',
  showCancelButton = true,
  observFunc = undefined,
  isObserveEnabled = false,
  secondaryButtonProps = {},
}) => {
  const internalFormRef = useRef<any>(null);
  const formRef = customFormRef ? customFormRef : internalFormRef;
  const [isFormValid, setFormValidity] = useState<boolean[]>(
    formRef.current?.isValid && (skipDirtyCheck ? true : formRef.current?.dirty)
  );

  const newFormConfigs = [
    ...formConfig,
    {
      type: 'custom',
      Field: ({ formik }) => {
        useEffect(() => {
          setFormValidity(
            formik?.isValid && (skipDirtyCheck ? true : formik?.dirty)
          );
        }, [formik]);
      },
    },
  ];

  const renderFooter = () => {
    const handlePrimaryCtaClick = () => {
      if (formRef?.current && withForm) {
        const errors = Object.keys(formRef.current?.errors || {});
        if (errors?.length > 0 && scrollOnError) {
          const element = document.getElementById(errors[0]);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
        formRef.current.submitForm();
      } else if (!withForm) {
        handleSubmit?.();
      }
    };

    return (
      <ModalFooter
        gap="6px"
        borderTop={enableBorder ? '1px solid ' : 'none'}
        borderColor={enableBorder ? 'modal-header-footer-border' : 'none'}
        {...modalFooterProps}
      >
        {showCancelButton && (
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        )}
        {showSecondaryButton && (
          <Button
            variant="secondary"
            onClick={handleSecondaryClick}
            data-test-id="secondary-cta-button"
            {...secondaryButtonProps}
          >
            {secondaryCtaText}
          </Button>
        )}
        {primaryCtaText && (
          <Button
            isLoading={isCtaLoading}
            variant="primary"
            disabled={
              isCtaLoading || (!skipCTADisabled && !isFormValid && withForm)
            }
            onClick={handlePrimaryCtaClick}
            data-test-id="primary-cta-button"
          >
            {primaryCtaText}
          </Button>
        )}
      </ModalFooter>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      autoFocus={autoFocus}
      size={modalSize}
      motionPreset="scale"
      scrollBehavior="outside"
    >
      {showOverlay && <ModalOverlay {...modalOverlayProps} />}
      <ModalContent
        borderRadius={{ base: '16px', md: 'sm' }}
        w={{ base: '90%', sm: '90%', md: '500px' }}
        maxW={{ base: '90%', sm: '90%', md: '500px' }}
        minW={{ base: '90%', sm: '90%', md: '500px' }}
        m="0 auto"
        ref={modalRef}
        {...modalContentProps}
      >
        {showCloseButton && (
          <ModalCloseButton
            bg="#fff"
            _hover={{
              bg: 'secondary-50',
            }}
            _focus={{
              boxShadow: 'none',
            }}
            onClick={(e) => {
              if (onCancel) {
                onCancel?.(e);
              } else {
                onClose?.();
              }
            }}
            top="4px"
            right="4px"
            color="secondary-700"
            pos="absolute"
            __css={{ svg: { color: 'secondary-700' } }}
          />
        )}
        {showHeader && (
          <ModalHeader
            borderBottom={enableBorder ? '1px solid ' : 'none'}
            borderColor={enableBorder ? 'modal-header-footer-border' : 'none'}
            {...modalHeaderProps}
          >
            <Flex justifyContent="space-between">
              <Flex flexDir="column">
                {typeof headerText === 'string' ? (
                  <KText variant="heading_2">{headerText}</KText>
                ) : (
                  headerText
                )}
                {subHeaderText && <KText mt="xs">{subHeaderText}</KText>}
              </Flex>
              {renderHeaderCta && renderHeaderCta?.()}
            </Flex>
          </ModalHeader>
        )}
        {showAdditionalBlock && AdditionalBlock}
        <ModalBody py="0" h="100%" {...modalBodyProps}>
          {withForm && (
            <Box {...formContainerStyles}>
              <FormBuilder
                initialValues={initialValues}
                config={newFormConfigs}
                onSubmit={handleSubmit}
                formRef={formRef}
                isObserveEnabled={isObserveEnabled}
                observFunc={observFunc}
              />
            </Box>
          )}
          {children && children}
        </ModalBody>
        {showAdditionalBlockAfter && AdditionalBlockAfter && (
          <AdditionalBlockAfter {...additionalBlockAfterProps} />
        )}
        {showFooter ? renderFooter() : <Box p="10px 0"></Box>}
      </ModalContent>
    </Modal>
  );
};
